import React from 'react';
import { ProcessingCardOverlay } from '../components/ProcessingIndicator';
import { PROCESSING_VARIANTS } from '../components/processing-variants';

/**
 * Internal-only preview route at /promo/processing.
 *
 * Renders the current Ken-Burns implementation alongside the 6
 * alternative variants (A-F) so the user can compare them side by
 * side. Each card is wrapped in a phone-style frame so the comparison
 * approximates what users will actually see in the Dashboard grid.
 */
export default function PlayProcessingPreview() {
    return (
        <div className="min-h-screen bg-zinc-100 py-10 px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
                <div className="mb-8">
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-2">Processing-state alternatives</h1>
                    <p className="text-sm text-muted-foreground max-w-2xl">
                        Internal preview. Compare the current Ken-Burns implementation against 6 alternatives, all
                        rendered in the same Dashboard-card frame so the comparison is apples-to-apples. Pick a
                        winner - message back the letter of the option you want me to ship.
                    </p>
                </div>

                {/* Current baseline */}
                <Section
                    title="0 - Current: Ken-Burns brain pan"
                    subtitle="Slow camera move over the brain illustration. Already shipped."
                    vibe="cinematic, premium"
                >
                    <div className="aspect-video rounded-2xl overflow-hidden">
                        <ProcessingCardOverlay stage="transcribing" url="https://www.facebook.com/reel/abc123" />
                    </div>
                </Section>

                {/* All 6 alternatives */}
                {PROCESSING_VARIANTS.map((v) => (
                    <Section
                        key={v.key}
                        title={v.title}
                        subtitle={v.subtitle}
                        vibe={v.vibe}
                    >
                        <v.Component />
                    </Section>
                ))}

                <div className="mt-12 p-5 rounded-2xl border border-emerald-200 bg-emerald-50/60">
                    <h3 className="font-heading font-semibold text-lg text-emerald-900 mb-1">My pick</h3>
                    <p className="text-sm text-emerald-900/85">
                        <strong>Option F (Skeleton fade-in)</strong> is the most polished and gives users the strongest
                        sense of progress because they literally see the bookmark being built. <strong>Option B (Live-typing)</strong>
                        is the biggest {'"'}wow{'"'} for first-time users. <strong>Option E (Sonar ripples)</strong> is the
                        calmest if you{"'"}re worried about motion fatigue on long lists. Tell me which one - or mix.
                    </p>
                </div>
            </div>
        </div>
    );
}

function Section({ title, subtitle, vibe, children }) {
    return (
        <div className="mb-10">
            <div className="flex items-baseline justify-between gap-3 mb-3">
                <div>
                    <h2 className="font-heading font-semibold text-lg sm:text-xl text-foreground">{title}</h2>
                    {subtitle && <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
                </div>
                {vibe && (
                    <span className="hidden sm:inline-block text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-white border border-border rounded-full px-2.5 py-1">
                        {vibe}
                    </span>
                )}
            </div>
            {/* Phone-shaped frame for the variant to live in */}
            <div className="max-w-md">
                {children}
            </div>
        </div>
    );
}
